import { isEmpty } from 'lodash';
import { useCallback } from 'react';

import useDebouncedEffect from '@fhs-legacy/frontend/src/hooks/use-debounce-effect';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { usePaymentContext } from '@fhs-legacy/frontend/src/state/payment';
import { mapPaymentMethodToLoyaltyPaymentMethods } from '@fhs-legacy/frontend/src/utils/payment';

import { useCartValues } from '../../state/cart-store';

export function useLoyaltyIncentivesEvaluation() {
  /*
   * It handles both offers and rewards evaluation, dispatching the results to the Redux store whenever cart changes
   * the 250ms debounce prevents excessive API calls;
   */
  const { cartEntries } = useCartValues();
  const { calculatedCartTotal } = useCartContext();
  const { loyaltyUserId } = useLoyaltyUser();
  const { checkoutPaymentMethod } = usePaymentContext();
  const dispatch = useAppDispatch();
  const { evaluateLoyaltyUserIncentives, evaluateLoyaltyUserRewards } = useLoyaltyContext();

  const { appliedLoyaltyDiscountOffer, appliedRewards, incentiveSelections, appliedLoyaltyOffers } =
    useAppSelector(state => ({
      appliedLoyaltyDiscountOffer: selectors.loyalty.selectDiscountAppliedCmsOffer(state),
      appliedRewards: selectors.loyalty.selectAppliedLoyaltyRewards(state),
      incentiveSelections: selectors.loyalty.selectSelectedOfferSelections(state),
      appliedLoyaltyOffers: selectors.loyalty.selectAppliedOffers(state),
    }));

  const runIncentivesEvaluation = useCallback(() => {
    if (!loyaltyUserId) {
      return;
    }
    const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

    if (appliedLoyaltyOffers.length) {
      evaluateLoyaltyUserIncentives({
        loyaltyId: loyaltyUserId,
        appliedLoyaltyOffers,
        cartEntries,
        appliedRewards,
        subtotalAmount: calculatedCartTotal,
        paymentMethod,
        appliedLoyaltyDiscountOffer,
        incentiveSelections,
      });
    }

    if (isEmpty(appliedRewards)) {
      dispatch(actions.loyalty.resetAppliedLoyaltyRewardsFeedbackMap());
      return;
    }

    evaluateLoyaltyUserRewards({
      loyaltyId: loyaltyUserId,
      appliedLoyaltyRewards: appliedRewards,
      cartEntries,
      subtotalAmount: calculatedCartTotal,
      paymentMethod,
    });
  }, [
    loyaltyUserId,
    appliedLoyaltyOffers,
    cartEntries,
    appliedRewards,
    calculatedCartTotal,
    checkoutPaymentMethod,
    appliedLoyaltyDiscountOffer,
    incentiveSelections,
    evaluateLoyaltyUserIncentives,
    evaluateLoyaltyUserRewards,
    dispatch,
  ]);

  useDebouncedEffect(runIncentivesEvaluation, 250, [
    loyaltyUserId,
    appliedLoyaltyOffers,
    cartEntries,
    appliedRewards,
    calculatedCartTotal,
    checkoutPaymentMethod,
    appliedLoyaltyDiscountOffer,
    incentiveSelections,
    evaluateLoyaltyUserIncentives,
    evaluateLoyaltyUserRewards,
    dispatch,
  ]);
}
